import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  GridLegacy as Grid,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { Event, Person, VideoCall, AccessTime } from '@mui/icons-material';
import moment from 'moment';
import { useAuth } from '../../utils/AuthContext';
import { panelistAPI, vendorAPI, adminAPI } from '../../services/api';
import { tokens, toneColors, getStatusTone } from '../../theme';

const { color: c, radius } = tokens;

/**
 * FullCalendar theming, scoped to this component.
 *
 * FullCalendar ships its own chrome — navy pill buttons, heavy grid lines,
 * saturated event fills — which looked nothing like the rest of the app. This
 * maps its CSS custom properties and a few classes onto our tokens to get the
 * calmer, denser treatment familiar from Teams/Outlook: hairline grid, quiet
 * day headers, a segmented view switcher, and events as light tinted blocks
 * with a coloured leading bar and dark readable text.
 */
const calendarSx = {
  // FullCalendar v6 custom properties
  '--fc-border-color': c.border,
  '--fc-page-bg-color': c.surface,
  '--fc-neutral-bg-color': c.surfaceSunken,
  '--fc-today-bg-color': c.accentSoft,
  '--fc-now-indicator-color': c.danger,
  '--fc-non-business-color': c.surfaceSunken,
  '--fc-small-font-size': '0.6875rem',

  '& .fc': { fontFamily: 'inherit', fontSize: '0.75rem' },

  // -- Toolbar -------------------------------------------------------------
  '& .fc .fc-toolbar.fc-header-toolbar': {
    marginBottom: 12,
    gap: 8,
    flexWrap: 'wrap',
  },
  '& .fc .fc-toolbar-title': {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: c.ink,
    letterSpacing: '-0.005em',
  },
  '& .fc .fc-button': {
    backgroundColor: c.surface,
    border: `1px solid ${c.borderStrong}`,
    color: c.inkSecondary,
    boxShadow: 'none',
    borderRadius: `${radius.sm}px`,
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'none',
    padding: '4px 10px',
    lineHeight: 1.5,
    '&:hover': { backgroundColor: c.surfaceHover, color: c.ink, borderColor: c.inkTertiary },
    '&:focus': { boxShadow: 'none' },
    '&:not(:disabled):active, &:not(:disabled).fc-button-active': {
      backgroundColor: c.accentSoft,
      borderColor: c.accentBorder,
      color: c.accent,
      boxShadow: 'none',
    },
    '&:disabled': { backgroundColor: c.surfaceSunken, color: c.inkDisabled, borderColor: c.border, opacity: 1 },
  },
  // Segmented control for month / week / day
  '& .fc .fc-button-group': {
    gap: 0,
    '& .fc-button:not(:last-child)': { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
    '& .fc-button:not(:first-of-type)': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      marginLeft: '-1px',
    },
  },
  '& .fc .fc-icon': { fontSize: '1rem', verticalAlign: 'middle' },

  // -- Grid ----------------------------------------------------------------
  '& .fc .fc-scrollgrid': { borderColor: c.border },
  '& .fc-theme-standard td, & .fc-theme-standard th': { borderColor: c.border },
  '& .fc .fc-col-header-cell': {
    backgroundColor: c.surfaceSunken,
    padding: '6px 4px',
  },
  '& .fc .fc-col-header-cell-cushion': {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: c.inkSecondary,
    padding: '2px 4px',
    textDecoration: 'none',
  },
  '& .fc .fc-day-today .fc-col-header-cell-cushion': { color: c.accent },
  // Time gutter
  '& .fc .fc-timegrid-axis-cushion, & .fc .fc-timegrid-slot-label-cushion': {
    fontSize: '0.6875rem',
    color: c.inkTertiary,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .fc .fc-timegrid-slot': { height: 30 },
  // Minor (half-hour) lines are dotted so the hour lines read as the structure
  '& .fc .fc-timegrid-slot-minor': { borderTopStyle: 'dotted', borderTopColor: c.border },
  '& .fc .fc-day-today': { backgroundColor: `${c.accentSoft} !important` },
  '& .fc .fc-timegrid-now-indicator-line': { borderWidth: '1px 0 0', borderColor: c.danger },
  '& .fc .fc-timegrid-now-indicator-arrow': { borderColor: c.danger, color: c.danger },
  '& .fc .fc-day-sat, & .fc .fc-day-sun': { backgroundColor: c.surfaceSunken },
  '& .fc .fc-daygrid-day-number': { fontSize: '0.6875rem', color: c.inkSecondary, padding: '4px 6px' },
  '& .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number': { color: c.accent, fontWeight: 600 },

  // -- Events --------------------------------------------------------------
  '& .fc .fc-event': {
    border: 'none',
    background: 'none',
    boxShadow: 'none',
    padding: 0,
    borderRadius: `${radius.sm}px`,
  },
  '& .fc .fc-event:focus-visible': { outline: `2px solid ${c.accent}`, outlineOffset: 1 },
  '& .fc .fc-event-main': { padding: 0, color: 'inherit' },
  '& .fc .fc-timegrid-event': { boxShadow: 'none', borderRadius: `${radius.sm}px` },
  '& .fc .fc-timegrid-event-harness': { marginRight: 2 },
};

const TalentFlowCalendar = ({
  userRole,
  height = 700,
  onBookingSuccess,
}) => {
  const { user } = useAuth();

  const [events, setEvents] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedInterviews, setBookedInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingData, setBookingData] = useState({
    candidate_id: null,
    candidate_name: '',
    candidate_email: '',
    job_id: null,
    job_title: '',
    interview_type: 'technical',
    booking_notes: '',
  });
  const [bookingProcessing, setBookingProcessing] = useState(false);

  // Approved candidates for selected panelist (vendor booking)
  const [approvedCandidates, setApprovedCandidates] = useState([]);
  const [approvedLoading, setApprovedLoading] = useState(false);

  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // ✅ FIXED: Enhanced data loading with detailed information
  const loadCalendarData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log(`🗓️ Loading calendar data for ${userRole}`);
      
      let slots = [];
      let interviews = [];

      switch (userRole) {
        case 'vendor':
          {
            const slotsResponse = await vendorAPI.getAvailableSlots({ only_available: true });
            slots = slotsResponse.data.slots || [];
            console.log(`📅 Vendor loaded ${slots.length} available slots`);

            try {
              const interviewsResponse = await vendorAPI.getInterviews();
              interviews = interviewsResponse.data.interviews || [];
            } catch (interviewError) {
              console.warn('⚠️ Could not load vendor interviews:', interviewError);
              interviews = [];
            }
          }
          break;

        case 'panelist':
          {
            const slotsResponse = await panelistAPI.getAvailabilitySlots({ include_booked: true });
            slots = slotsResponse.data.slots || slotsResponse.data.availability_slots || [];
            console.log(`📅 Panelist loaded ${slots.length} slots`);

            try {
              const interviewsResponse = await panelistAPI.getUpcomingInterviews();
              interviews = interviewsResponse.data.upcoming_interviews || interviewsResponse.data.interviews || [];
            } catch (interviewError) {
              console.warn('⚠️ Could not load panelist interviews:', interviewError);
              interviews = [];
            }
          }
          break;

        case 'admin':
          {
            const slotsResponse = await adminAPI.getExpertSlots({});
            slots = slotsResponse.data.expert_slots || slotsResponse.data.slots || [];
            console.log(`📅 Admin loaded ${slots.length} expert slots`);

            try {
              const interviewsResponse = await adminAPI.getInterviews();
              interviews = interviewsResponse.data.interviews || [];
            } catch (interviewError) {
              console.warn('⚠️ Could not load admin interviews:', interviewError);
              interviews = [];
            }
          }
          break;

        default:
          throw new Error(`Unsupported user role: ${userRole}`);
      }

      // ✅ ENHANCED: Slot data normalization with detailed information
      const normalizedSlots = slots
        .filter(s => s && (s.id || s.slot_id || s.expert_slot_id))
        .map(slot => {
          const id = slot.id || slot.slot_id || slot.expert_slot_id;
          
          const start = slot.start_datetime || slot.available_from || slot.start || slot.from;
          const end = slot.end_datetime || slot.available_to || slot.end || slot.to;
          
          // This was hardcoded to the string 'Unknown Panelist', so every slot
          // in every calendar claimed to have no owner. Resolve it the same way
          // the email below is resolved.
          const panelistName =
            slot.panelist_name ||
            (slot.panelist && (slot.panelist.username || slot.panelist.name)) ||
            slot.username ||
            'Unassigned';

          const panelistEmail = slot.panelist_email ||
                               (slot.panelist && slot.panelist.email) ||
                               slot.email || '';
          
          // ✅ ENHANCED: Extract booking information
          const bookedByName = slot.booked_by_name || slot.vendor_name || slot.booked_by || '';
          const candidateName = slot.candidate_name || '';
          const jobTitle = slot.job_title || '';
          
          const isBooked = slot.is_booked || false;
          const isAvailable = slot.is_available !== false;

          if (!id || !start || !end) {
            console.warn('⚠️ Skipping invalid slot:', slot);
            return null;
          }

          const duration = slot.duration_minutes || 
                          Math.round((new Date(end) - new Date(start)) / 60000) || 60;

          // A panelist looking at their own availability doesn't need their own
          // name repeated on every block; other roles do.
          const ownSlot = userRole === 'panelist';
          const title = isBooked
            ? (ownSlot ? 'Booked' : `${panelistName} — booked`)
            : (ownSlot ? 'Available' : panelistName);

          return {
            id,
            title,
            start: new Date(start),
            end: new Date(end),
            extendedProps: {
              ...slot,
              // Colour is applied in eventContent from this tone, so blocks use
              // a light tint with readable dark text rather than white text on
              // a saturated fill.
              tone: isBooked ? 'warning' : 'success',
              panelist_name: panelistName,
              panelist_email: panelistEmail,
              booked_by_name: bookedByName,
              candidate_name: candidateName,
              job_title: jobTitle,
              duration_minutes: duration,
              is_booked: isBooked,
              is_available: isAvailable,
              can_book: userRole === 'vendor' && isAvailable && !isBooked,
              type: isBooked ? 'booked_slot' : 'available_slot',
              status: isBooked ? 'Booked' : 'Available'
            },
          };
        })
        .filter(Boolean);

      // ✅ ENHANCED: Interview data normalization with detailed information
      const normalizedInterviews = interviews
        .filter(i => i && i.id && (i.scheduled_at || i.start))
        .map(interview => {
          const startTime = interview.scheduled_at || interview.start;
          const start = new Date(startTime);
          const duration = interview.duration_minutes || 60;
          const end = new Date(start.getTime() + duration * 60000);

          const candidateName = interview.candidate_name ||
                               (interview.candidate && interview.candidate.name) ||
                               'Candidate';

          const panelistName = interview.panelist_name ||
                              (interview.panelist && (interview.panelist.username || interview.panelist.name)) ||
                              'Unassigned';

          const vendorName = interview.vendor_name || interview.booked_by || '';

          return {
            id: `interview-${interview.id}`,
            title: candidateName,
            start,
            end,
            extendedProps: {
              ...interview,
              // Uses the app-wide status vocabulary so an interview's colour
              // matches how the same status renders elsewhere.
              tone: getStatusTone(interview.status || 'scheduled'),
              type: 'booked_interview',
              candidate_name: candidateName,
              panelist_name: panelistName,
              vendor_name: vendorName,
              status: interview.status || 'Scheduled'
            },
          };
        });

      setAvailableSlots(normalizedSlots);
      setBookedInterviews(normalizedInterviews);
      
      const finalEvents = userRole === 'vendor' ? 
        [...normalizedSlots.filter(slot => slot.extendedProps.can_book), ...normalizedInterviews] :
        [...normalizedSlots, ...normalizedInterviews];
      
      setEvents(finalEvents);
      
      console.log(`✅ Calendar loaded: ${normalizedSlots.length} slots, ${normalizedInterviews.length} interviews`);

    } catch (error) {
      console.error('❌ Failed to load calendar data:', error);
      setError(error.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  // ✅ ENHANCED: Event click handler with detailed info
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const props = event.extendedProps;

    if (props.type === 'available_slot' && userRole === 'vendor' && props.can_book) {
      setSelectedSlot(props);
      setBookingData({
        candidate_id: null,
        candidate_name: '',
        candidate_email: '',
        job_id: null,
        job_title: '',
        interview_type: 'technical',
        booking_notes: ''
      });
      // Load approved candidates for this panelist to populate dropdown
      (async () => {
        try {
          setApprovedLoading(true);
          setApprovedCandidates([]);
          if (props.panelist_id) {
            const resp = await vendorAPI.getPanelistAvailability(props.panelist_id);
            const list = resp?.data?.available_candidates || [];
            setApprovedCandidates(Array.isArray(list) ? list : []);
          }
        } catch (e) {
          console.warn('Failed to load approved candidates:', e);
          setApprovedCandidates([]);
        } finally {
          setApprovedLoading(false);
        }
      })();
      setBookingDialogOpen(true);
    } else {
      setSelectedEvent(props);
      setEventDialogOpen(true);
    }
  };

  // ✅ ENHANCED: Booking submission 
  const handleBookingSubmit = async () => {
    if (!selectedSlot || !bookingData.candidate_id || !bookingData.job_id) {
      alert('Please fill in all required fields');
      return;
    }

    setBookingProcessing(true);

    try {
      const bookingPayload = {
        slot_id: selectedSlot.id || selectedSlot.slot_id,
        candidate_id: bookingData.candidate_id,
        candidate_name: bookingData.candidate_name,
        candidate_email: bookingData.candidate_email,
        job_id: bookingData.job_id,
        job_title: bookingData.job_title,
        interview_type: bookingData.interview_type,
        booking_notes: bookingData.booking_notes,
      };

      await vendorAPI.bookInterviewWithTeams(bookingPayload);

      alert('Interview slot booked successfully with Teams meeting!');
      setBookingDialogOpen(false);
      setBookingData({
        candidate_id: null,
        candidate_name: '',
        candidate_email: '',
        job_id: null,
        job_title: '',
        interview_type: 'technical',
        booking_notes: ''
      });
      
      await loadCalendarData();
      
      if (onBookingSuccess) onBookingSuccess(selectedSlot);
      
    } catch (error) {
      console.error('❌ Booking failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to book interview slot';
      alert(`Failed to book interview: ${errorMessage}`);
    } finally {
      setBookingProcessing(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* A quiet inline key, using the same tints as the blocks themselves,
          instead of three saturated pills in their own panel. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 1.5,
          rowGap: 0.5,
        }}
      >
        {[
          { label: 'Available', tone: 'success' },
          { label: 'Booked', tone: 'warning' },
          { label: 'Interview', tone: 'info' },
        ].map(({ label, tone }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                bgcolor: toneColors[tone].bg,
                borderLeft: `3px solid ${toneColors[tone].fg}`,
              }}
            />
            <Typography variant="body2" sx={{ color: c.inkSecondary }}>
              {label}
            </Typography>
          </Box>
        ))}
        <Typography variant="body2" sx={{ color: c.inkTertiary, ml: 'auto' }}>
          {userRole === 'vendor'
            ? 'Select an available slot to book an interview'
            : 'Hover a block for full details'}
        </Typography>
      </Box>

      <Box sx={calendarSx}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          height={height}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          events={events}
          eventClick={handleEventClick}
          eventContent={(eventInfo) => {
            const props = eventInfo.event.extendedProps;
            const tone = toneColors[props.tone] || toneColors.info;
            const isMonthView = eventInfo.view.type === 'dayGridMonth';

            // Two lines at most. The previous renderer stacked a title, a
            // panelist line, a candidate line, a vendor line and a chip into a
            // ~40px block, so the text overflowed and collided. Everything
            // beyond the essentials lives in the hover tooltip instead.
            const timeLabel = eventInfo.timeText;

            if (isMonthView) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0,
                    px: 0.5,
                    py: '1px',
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tone.fg, flexShrink: 0 }} />
                  <Box component="span" sx={{ fontSize: '0.6875rem', color: c.inkSecondary, flexShrink: 0 }}>
                    {timeLabel}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 500,
                      color: c.ink,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {eventInfo.event.title}
                  </Box>
                </Box>
              );
            }

            return (
              <Box
                sx={{
                  height: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  bgcolor: tone.bg,
                  borderLeft: `3px solid ${tone.fg}`,
                  borderRadius: `${radius.sm}px`,
                  px: 0.75,
                  py: 0.375,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  cursor: props.can_book ? 'pointer' : 'default',
                  transition: 'filter 120ms',
                  '&:hover': { filter: 'brightness(0.97)' },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: tone.fg,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {eventInfo.event.title}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.6875rem',
                    lineHeight: 1.3,
                    color: c.inkSecondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {timeLabel}
                </Box>
              </Box>
            );
          }}
          eventDidMount={(info) => {
            const props = info.event.extendedProps;

            // The blocks themselves show only a title and time, so the full
            // record lives here.
            const lines = [];
            const when = info.event.start
              ? moment(info.event.start).format('ddd D MMM, h:mm A')
              : null;
            if (when) lines.push(when);
            lines.push(`Panelist: ${props.panelist_name}`);
            if (props.panelist_email) lines.push(`Email: ${props.panelist_email}`);

            if (props.is_booked || props.type === 'booked_interview') {
              if (props.candidate_name) lines.push(`Candidate: ${props.candidate_name}`);
              if (props.job_title) lines.push(`Role: ${props.job_title}`);
              if (props.booked_by_name || props.vendor_name) {
                lines.push(`Booked by: ${props.booked_by_name || props.vendor_name}`);
              }
            }

            lines.push(`Duration: ${props.duration_minutes || 60} min`);
            if (props.status) lines.push(`Status: ${props.status}`);
            if (userRole === 'vendor' && props.can_book) {
              lines.push('');
              lines.push('Click to book this slot');
            }

            info.el.title = lines.join('\n');
            info.el.style.cursor = userRole === 'vendor' && props.can_book ? 'pointer' : 'default';
          }}
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5],
            startTime: '09:00',
            endTime: '18:00'
          }}
          weekends={true}
          nowIndicator={true}
          eventDisplay="block"
          // -- Teams-like reading conventions --------------------------------
          firstDay={1}                     // weeks start Monday
          dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
          slotDuration="00:30:00"
          slotLabelInterval="01:00"
          slotLabelFormat={{ hour: 'numeric', minute: '2-digit', omitZeroMinute: true, meridiem: 'short' }}
          eventTimeFormat={{ hour: 'numeric', minute: '2-digit', omitZeroMinute: true, meridiem: 'short' }}
          allDaySlot={false}               // nothing in this app is all-day
          expandRows                       // fill the height instead of leaving dead space
          scrollTime="08:00:00"
          nowIndicatorClassNames="tf-now"
          dayMaxEventRows={3}              // month view stays legible
          eventOrder="start,-duration,title"
          buttonText={{ today: 'Today', month: 'Month', week: 'Week', day: 'Day' }}
          stickyHeaderDates
        />
      </Box>

      {/* ✅ ENHANCED: Booking Dialog with detailed slot info */}
      <Dialog open={bookingDialogOpen} onClose={() => setBookingDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Book Interview Slot
          <Chip label={selectedSlot?.panelist_name || 'Panelist'} sx={{ ml: 2 }} color="primary" />
        </DialogTitle>
        <DialogContent>
          {selectedSlot && (
            <Box>
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" gutterBottom><strong>Slot Details</strong></Typography>
                <Typography variant="body2">
                  <strong>Panelist:</strong> {selectedSlot.panelist_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedSlot.panelist_email || 'Not available'}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {moment(selectedSlot.start_datetime || selectedSlot.available_from).format('dddd, MMMM DD, YYYY')}
                </Typography>
                <Typography variant="body2">
                  <strong>⏰ Time:</strong> {moment(selectedSlot.start_datetime || selectedSlot.available_from).format('h:mm A')} - 
                  {moment(selectedSlot.end_datetime || selectedSlot.available_to).format('h:mm A')}
                </Typography>
                <Typography variant="body2">
                  <strong>Duration:</strong> {selectedSlot.duration_minutes} minutes
                </Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel id="candidate-select-label">Candidate Name</InputLabel>
                    <Select
                      labelId="candidate-select-label"
                      label="Candidate Name"
                      displayEmpty
                      value={approvedCandidates.find(c => c.name === bookingData.candidate_name)?.id || ''}
                      onChange={(e) => {
                        const sel = approvedCandidates.find(c => c.id === e.target.value);
                        if (sel) {
                          setBookingData({
                            ...bookingData,
                            candidate_id: sel.id,
                            candidate_name: sel.name || '',
                            candidate_email: sel.email || '',
                            job_id: (sel.job && sel.job.id) || null,
                            job_title: (sel.job && sel.job.title) || bookingData.job_title || ''
                          });
                        } else {
                          setBookingData({ ...bookingData, candidate_id: null, candidate_name: '' });
                        }
                      }}
                      disabled={approvedLoading}
                    >
                      <MenuItem value="">
                        <em>{approvedLoading ? 'Loading…' : (approvedCandidates.length ? 'Select candidate' : 'No approved candidates')}</em>
                      </MenuItem>
                      {!approvedLoading && approvedCandidates.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Candidate Email"
                    type="email"
                    value={bookingData.candidate_email}
                    onChange={(e) => setBookingData({ ...bookingData, candidate_email: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    value={bookingData.job_title}
                    onChange={(e) => setBookingData({ ...bookingData, job_title: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Interview Type</InputLabel>
                    <Select
                      value={bookingData.interview_type}
                      label="Interview Type"
                      onChange={(e) => setBookingData({ ...bookingData, interview_type: e.target.value })}
                    >
                      <MenuItem value="technical">Technical</MenuItem>
                      <MenuItem value="behavioral">Behavioral</MenuItem>
                      <MenuItem value="screening">Screening</MenuItem>
                      <MenuItem value="final">Final Round</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Booking Notes (Optional)"
                    multiline
                    rows={3}
                    value={bookingData.booking_notes}
                    onChange={(e) => setBookingData({ ...bookingData, booking_notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDialogOpen(false)} disabled={bookingProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleBookingSubmit}
            disabled={bookingProcessing || !bookingData.candidate_id || !bookingData.job_id}
            variant="contained"
            color="primary"
            startIcon={bookingProcessing ? <CircularProgress size={20} /> : <Event />}
          >
            {bookingProcessing ? 'Booking...' : 'Book Interview'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ ENHANCED: Event Details Dialog with all information */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedEvent?.type === 'booked_interview' ? 'Interview details' : 'Slot details'}
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1"><strong>Panelist:</strong> {selectedEvent.panelist_name || 'N/A'}</Typography>
                  <Typography variant="body1"><strong>Panelist Email:</strong> {selectedEvent.panelist_email || 'N/A'}</Typography>
                  
                  {selectedEvent.candidate_name && (
                    <Typography variant="body1"><strong>Candidate:</strong> {selectedEvent.candidate_name}</Typography>
                  )}
                  {selectedEvent.candidate_email && (
                    <Typography variant="body1"><strong>Candidate Email:</strong> {selectedEvent.candidate_email}</Typography>
                  )}
                  {selectedEvent.job_title && (
                    <Typography variant="body1"><strong>Job Title:</strong> {selectedEvent.job_title}</Typography>
                  )}
                  {(selectedEvent.booked_by_name || selectedEvent.vendor_name) && (
                    <Typography variant="body1"><strong>Booked By:</strong> {selectedEvent.booked_by_name || selectedEvent.vendor_name}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">
                    <strong>Date:</strong> {moment(selectedEvent.scheduled_at || selectedEvent.start_datetime).format('dddd, MMMM DD, YYYY')}
                  </Typography>
                  <Typography variant="body1">
                    <strong>⏰ Time:</strong> {moment(selectedEvent.scheduled_at || selectedEvent.start_datetime).format('h:mm A')} - 
                    {selectedEvent.end_datetime ? 
                      moment(selectedEvent.end_datetime).format('h:mm A') :
                      moment(selectedEvent.scheduled_at).add(selectedEvent.duration_minutes || 60, 'minutes').format('h:mm A')
                    }
                  </Typography>
                  <Typography variant="body1"><strong>Duration:</strong> {selectedEvent.duration_minutes || 60} minutes</Typography>
                  {selectedEvent.status && (
                    <Typography variant="body1">
                      <strong>Status:</strong> 
                      <Chip 
                        label={selectedEvent.status} 
                        size="small" 
                        color={selectedEvent.is_booked ? 'error' : 'success'} 
                        sx={{ ml: 1 }} 
                      />
                    </Typography>
                  )}
                  {selectedEvent.interview_type && (
                    <Typography variant="body1"><strong>🔍 Type:</strong> {selectedEvent.interview_type}</Typography>
                  )}
                </Grid>
              </Grid>

              {selectedEvent.meeting_link && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VideoCall />}
                    onClick={() => window.open(selectedEvent.meeting_link, '_blank')}
                    fullWidth
                  >
                    Join Teams Meeting
                  </Button>
                </Box>
              )}

              {selectedEvent.booking_notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Notes:</strong> {selectedEvent.booking_notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TalentFlowCalendar;
